import React, { useState, useEffect, useCallback } from 'react';
import { FaCrown, FaCheck } from 'react-icons/fa';
import '../../styles/landing-page/PlansSection.css';

const PlansSection = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const currencyRates = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    INR: 83.12,
    JPY: 149.50,
    AUD: 1.52,
    CAD: 1.36
  };

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$'
  };

  const getDefaultPlans = useCallback(() => {
    return [
      {
        _id: 1,
        name: 'Starter',
        price: 99,
        originalPrice: null,
        assets: 10,
        users: 5,
        currency: 'USD',
        description: 'Perfect for small teams getting started',
        features: [
          'Basic Asset Tracking',
          'Email Support',
          'Monthly Reports'
        ],
        highlighted: false,
        cta: 'Get Started'
      },
      {
        _id: 2,
        name: 'Professional',
        price: 299,
        originalPrice: 399,
        assets: 50,
        users: 20,
        currency: 'USD',
        description: 'Ideal for growing businesses',
        features: [
          'Advanced Tracking',
          'Priority Support',
          'Maintenance Logs',
          'Custom Reports',
          'API Access'
        ],
        highlighted: true,
        cta: 'Start Free Trial'
      },
      {
        _id: 3,
        name: 'Enterprise',
        price: 999,
        originalPrice: null,
        assets: 999,
        users: 999,
        currency: 'USD',
        description: 'For large-scale operations',
        features: [
          'Full-Featured Platform',
          '24/7 Dedicated Support',
          'Advanced Analytics',
          'Custom Integrations',
          'White-label Options',
          'SLA Guarantee'
        ],
        highlighted: false,
        cta: 'Contact Sales'
      }
    ];
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiUrl}/api/plans`);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      const data = await response.json();
      // Use returned plans if they exist, otherwise use fallback
      if (data && Array.isArray(data) && data.length > 0) {
     
        setPlans(data);
      } else {
     
        setPlans(getDefaultPlans());
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Fallback to default plans if API fails
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  }, [getDefaultPlans]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const getPrice = (plan) => {
    if (billingCycle === 'yearly') {
      return Math.round(plan.price * 12 * 0.83); // 17% discount for yearly
    }
    return plan.price;
  };

  const handleCTA = (planName) => {
   
    // TODO: Implement checkout/trial flow
  };

  if (loading) {
    return (
      <section id="plans" className="plans-section">
        <div className="plans-container">
          <div className="plans-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Choose the perfect plan for your asset management needs</p>
          </div>
          <div className="loading-message" style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <p>Loading plans...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="plans" className="plans-section">
      <div className="plans-container">
        <div className="plans-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Choose the perfect plan for your asset management needs</p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="billing-toggle">
              <button
                className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('yearly')}
              >
                Yearly
                <span className="save-badge">Save 17%</span>
              </button>
            </div>

            <div>
              <label style={{ fontSize: '14px', color: '#666', marginRight: '8px', fontWeight: '600' }}>Display Currency:</label>
              <select 
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
              >
                {Object.keys(currencySymbols).map(curr => (
                  <option key={curr} value={curr}>{curr} ({currencySymbols[curr]})</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="plans-grid">
          {plans.map(plan => (
            <div
              key={plan._id || plan.id}
              className={`plan-card ${plan.highlighted ? 'highlighted' : ''}`}
            >
              {plan.highlighted && (
                <div className="popular-badge">
                  <FaCrown /> MOST POPULAR
                </div>
              )}

              <div className="plan-header">
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="plan-pricing">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  {plan.originalPrice && (
                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '14px' }}>
                      {currencySymbols[selectedCurrency]}{(plan.originalPrice * currencyRates[selectedCurrency]).toFixed(0)}
                    </span>
                  )}
                  <span className="currency">{currencySymbols[selectedCurrency]}</span>
                  <span className="price">{(getPrice(plan) * currencyRates[selectedCurrency]).toFixed(0)}</span>
                </div>
                <span className="period">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <div style={{ 
                  background: '#e8f4f8', 
                  padding: '4px 12px', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  color: '#0369a1',
                  fontWeight: '600'
                }}>
                  📦 {plan.assets === 999 ? 'Unlimited' : plan.assets} Assets
                </div>
                <div style={{ 
                  background: '#f0e8f8', 
                  padding: '4px 12px', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  color: '#6f42c1',
                  fontWeight: '600'
                }}>
                  👥 {plan.users === 999 ? 'Unlimited' : plan.users} Users
                </div>
              </div>

              <button
                className="plan-cta-btn"
                onClick={() => handleCTA(plan.name)}
              >
                {plan.cta}
              </button>

              <div className="plan-features">
                <p className="features-title">What's Included:</p>
                <ul>
                  {plan.features.map((feature, idx) => (
                    <li key={`${plan._id || plan.id}-feature-${idx}`}>
                      <FaCheck className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="plans-footer">
          <p>All plans include a 14-day free trial. No credit card required.</p>
          <p>Need a custom plan? <a href="#contact">Contact our sales team</a></p>
        </div>
      </div>
    </section>
  );
};

export default PlansSection;
